import { Spinner, Tooltip } from '@dixa/lib-shared-habla';
import { AsyncStatus } from 'lib-shared-types-api';
import type {
  ConversationMessageDeliveryEvent,
  ConversationMessageDeliveryEventComputed,
} from 'lib-shared-types-api';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useDeepCompareEffect, useFirstMountState } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { getConversationMessageDeliveryStatusAttributes } from 'utils/conversation';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ConversationMessageDeliveryReport } from '../conversationMessageDeliveryReport';

import styles from './conversationMessageDeliveryStatus.module.scss';

type ConversationMessageDeliveryStatusProps = {
  /** The id of the conversation the message belongs to. */
  csid: number;
  /** The id of the message to fetch the delivery status for. */
  messageId: string;
  /** The computed delivery event. */
  computedDeliveryEvent: ConversationMessageDeliveryEventComputed;
};

/**
 * Represents the component for displaying a computed conversation message
 * delivery status with an extended delivery status in a tooltip.
 */
export const ConversationMessageDeliveryStatus = (
  props: ConversationMessageDeliveryStatusProps,
): JSX.Element => {
  const dispatch = useAppDispatch();
  const isFirstMount = useFirstMountState();
  const { computedDeliveryEvent, csid, messageId } = props;
  const isTooltipOpen = useRef(false);
  const shouldRefetch = useRef(true);

  const [deliveryEvents, setDeliveryEvents] = useState<
    readonly ConversationMessageDeliveryEvent[]
  >([]);
  const [readDeliveryEventsAsyncStatus, setReadDeliveryEventsAsyncStatus] =
    useState<AsyncStatus>('uninitialized');

  const fetchDeliveryEvents = useCallback(async () => {
    setReadDeliveryEventsAsyncStatus('pending');

    const response = await dispatch(
      conversationsApi.endpoints.readConversationMessageDeliveryEvents.initiate(
        { csid, messageId, filter: 'latest_event_type' },
        { forceRefetch: true },
      ),
    );

    if (response.data) {
      setDeliveryEvents(response.data.data.delivery_events);
      setReadDeliveryEventsAsyncStatus('fulfilled');
    } else {
      setReadDeliveryEventsAsyncStatus('rejected');
    }

    shouldRefetch.current = false;
  }, [csid, dispatch, messageId]);

  const handleTooltipOpenChange = useCallback(
    async (isOpen: boolean) => {
      isTooltipOpen.current = isOpen;

      if (
        isOpen &&
        shouldRefetch.current &&
        readDeliveryEventsAsyncStatus !== 'pending'
      ) {
        await fetchDeliveryEvents();
      }
    },
    [fetchDeliveryEvents, readDeliveryEventsAsyncStatus],
  );

  const { icon } = getConversationMessageDeliveryStatusAttributes(
    computedDeliveryEvent.status,
  );

  const tooltipContent: ReactNode = useMemo(() => {
    if (
      readDeliveryEventsAsyncStatus === 'uninitialized' ||
      readDeliveryEventsAsyncStatus === 'pending'
    ) {
      return <Spinner size={16} />;
    }

    if (readDeliveryEventsAsyncStatus === 'rejected') {
      return 'Failed getting delivery status. Try again.';
    }

    if (!deliveryEvents.length) {
      return computedDeliveryEvent.status === 'sent'
        ? 'Sent'
        : 'No delivery status available. Try again later.';
    }

    return (
      <ConversationMessageDeliveryReport
        computedStatus={computedDeliveryEvent.status}
        deliveryEvents={deliveryEvents}
      />
    );
  }, [
    readDeliveryEventsAsyncStatus,
    deliveryEvents,
    computedDeliveryEvent.status,
  ]);

  useDeepCompareEffect(() => {
    if (isFirstMount) {
      return;
    }

    shouldRefetch.current = true;

    if (isTooltipOpen.current) {
      fetchDeliveryEvents();
    }
  }, [computedDeliveryEvent]);

  return (
    <Tooltip
      dark
      wrap="span"
      wrapProps={{
        className: styles.root,
      }}
      content={tooltipContent}
      onOpenChange={handleTooltipOpenChange}
    >
      {icon}
    </Tooltip>
  );
};
